"use client";

import { useEffect, useState } from "react";
import { api, type LaunchOffer, type PlanPromo } from "@/lib/api";

/**
 * The launch offer in the browser (admin → Discount offer; the server side is services/billing/promo.py).
 * The pricing data carries the offer a little before it starts, so a page cached for a few minutes still switches
 * on and off on time: everything here is decided by the clock, and nothing is shown before the page has mounted
 * (the server-rendered HTML never shows a price the buyer might not get).
 */

/** The current time, ticking every `everyMs` while `on`. Null until mounted. */
export function useNow(on = true, everyMs = 1000): number | null {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => {
    setNow(Date.now());
    if (!on) return;
    const id = window.setInterval(() => setNow(Date.now()), everyMs);
    return () => window.clearInterval(id);
  }, [on, everyMs]);
  return now;
}

export function offerLive(p: { starts_at: string; ends_at: string } | null | undefined, now: number | null): boolean {
  if (!p || now == null) return false;
  const start = Date.parse(p.starts_at);
  const end = Date.parse(p.ends_at);
  return Number.isFinite(start) && Number.isFinite(end) && now >= start && now < end;
}

/** "2d 4h", "5h 12m", "12m 05s", "45s". */
export function timeLeft(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

/** How long the discount lasts: "first 3 months", "first month", "first year". */
export function promoTerms(p: PlanPromo): string {
  if (p.interval === "YEARLY") return "first year";
  if (p.interval === "WEEKLY") return p.periods > 1 ? `first ${p.periods} weeks` : "first week";
  return p.periods > 1 ? `first ${p.periods} months` : "first month";
}

/** After the discounted period: "/mo after 3 months", "/year after the first year". */
export function promoThen(p: PlanPromo): string {
  if (p.interval === "YEARLY") return "/year from year 2";
  if (p.interval === "WEEKLY") return `/week after ${p.periods > 1 ? `${p.periods} weeks` : "the first week"}`;
  return `/mo after ${p.periods > 1 ? `${p.periods} months` : "the first month"}`;
}

let offerRequest: Promise<LaunchOffer | null> | null = null;

/** The running discount offer (admin → Discount offer), fetched once per page load and shared by the top bar and
 *  the popup. Null while loading or when there is none. */
export function useOffer(): Extract<LaunchOffer, { active: true }> | null {
  const [offer, setOffer] = useState<LaunchOffer | null>(null);
  useEffect(() => {
    let alive = true;
    offerRequest ??= api.getPromo().catch(() => null);
    offerRequest.then((o) => alive && setOffer(o));
    return () => {
      alive = false;
    };
  }, []);
  return offer && offer.active ? offer : null;
}

/** Hours, minutes and seconds left, two digits each, for the countdown blocks. */
export function clockParts(ms: number): { d: number; h: string; m: string; s: string } {
  const t = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(t / 86400);
  const pad = (n: number) => String(n).padStart(2, "0");
  return { d, h: pad(Math.floor((t % 86400) / 3600)), m: pad(Math.floor((t % 3600) / 60)), s: pad(t % 60) };
}

/** Copies text; true when it worked. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
